import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  Activity,
  TrendingUp,
  FileText,
  BarChart3
} from 'lucide-react';
import { cn } from '../../lib/utils';
import PortfolioHealthScreen from './PortfolioHealthScreen';
import PerformanceReportingScreen from './PerformanceReportingScreen';
import ClientStatementsScreen from './ClientStatementsScreen';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
`;

type TabId = 'health' | 'performance' | 'statements';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const tabs: Tab[] = [
  {
    id: 'health',
    label: 'Portfolio Health',
    icon: <Activity className="w-4 h-4" />,
    description: 'Risk scores, allocation drift, and concentration analysis'
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: <TrendingUp className="w-4 h-4" />,
    description: 'TWR/MWR returns with fee transparency'
  },
  {
    id: 'statements',
    label: 'Client Statements',
    icon: <FileText className="w-4 h-4" />,
    description: 'Generate and manage client statements'
  },
];

const RetailAnalyticsHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('health');

  return (
    <Container>
      {/* Header */}
      <div className="px-8 pt-6 pb-4 border-b border-white/5 bg-slate-950/50">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                <BarChart3 className="w-7 h-7 text-emerald-500" />
                Analytics & Reporting
                <span className="ml-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs font-bold text-emerald-400 uppercase tracking-widest">
                  RIA
                </span>
              </h1>
              <p className="text-slate-500 font-medium mt-1">
                Portfolio health, performance metrics, and client reporting
              </p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-bold transition-all relative group",
                  activeTab === tab.id
                    ? "text-emerald-400 bg-emerald-500/10 border border-emerald-500/20"
                    : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                )}
              >
                <span className={cn(
                  "transition-transform group-hover:scale-110",
                  activeTab === tab.id && "text-emerald-400"
                )}>
                  {tab.icon}
                </span>
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="retail-analytics-tab"
                    className="absolute -bottom-[1px] left-4 right-4 h-[2px] bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                    initial={false}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-[1800px] mx-auto">
        {activeTab === 'health' && <PortfolioHealthContent />}
        {activeTab === 'performance' && <PerformanceReportingScreen />}
        {activeTab === 'statements' && <ClientStatementsScreen />}
      </div>
    </Container>
  );
};

// Wrapper to render PortfolioHealthScreen without its own container padding
const PortfolioHealthContent: React.FC = () => {
  return <PortfolioHealthScreen />;
};

export default RetailAnalyticsHub;
